<template>
  <div class="incremental-onboarding-view-container">
    <div
      class="onboarding-view no-sidebar"
    >
      <div class="top-fixed">
        <IncrementalOnboardingHeader :dismiss="() => showDismissConfirmation = true" :current-step="currentStep" />
      </div>
      <div class="s-flex s-gap-8 s-p-6 s-py-0">
        <div class="s-p-4 s-fixed s-top-4" style="width: 256px; margin-top: 10px;">
          <div style="text-align: left; margin-bottom: 10px">
            See the
            <s-link
              variant="primary"
              href="https://www.chargebee.com/embedded-payments-fs/"
              target="_blank"
              style="display: inline-flex; align-items: center; gap: 4px"
            >
              Fee Schedule <s-icon name="open-in-new" size="12" />
            </s-link>
          </div>
          <ProgressSidebar
            :steps="steps"
            :current-step-index="currentStep"
            :completed-until-step="completedUntilStep"
            @handleClick="handleStepChange"
            @update:currentStep="(i) => currentStep = i"
          />
        </div>
        <!-- Main Content -->
        <div class="s-flex-1 s-p-6" style="margin-left: 350px; margin-top:10px; padding-bottom: 300px; max-width: 600px">
          <section class="s-rounded-2xl s-mx-auto s-p-6 s-mt-10 s-text-left" v-if="currentStep === -1">
            <h1 class="s-text-xl s-font-bold s-mb-4">
              Set up Chargebee Pay
            </h1>
            <div class="s-leading-snug s-text-neutral-400">
              Prepare the details to speed up your Chargebee Pay onboarding.
            </div>

            <div class="s-mt-4 s-mb-4" background="">
              <h1 class="s-text-lg s-font-bold s-mb-4 ">
                Details required to onboard on Chargebee Pay:
              </h1>

              <ul class="s-space-y-6 s-text-gray-800 s-text-base s-leading-relaxed s-flex s-flex-col s-gap-2">
                <li>
                  <div class="s-font-semibold">
                    Basic Company details
                  </div>
                  <div class="s-text-neutral-400">
                    Legal name, type, location and address.
                  </div>
                </li>
                <li>
                  <div class="s-font-semibold">
                    Profile
                  </div>
                  <div class="s-text-neutral-400">
                    Website, phone and merchant category code.
                  </div>
                </li>
                <li>
                  <div class="s-font-semibold">
                    Ownership Details
                  </div>
                  <div class="s-text-neutral-400">
                    Personal info, address, ownership and tax details.
                  </div>
                  <div class="s-text-neutral-400">
                    Passport, ID, Driver License. (Optional, in case of
                    additional verification needed).
                  </div>
                </li>

                <li>
                  <div class="s-font-semibold">
                    Registration
                  </div>
                  <div class="s-text-neutral-400">
                    EIN and incorporation date.
                  </div>
                </li>

                <li>
                  <div class="s-font-semibold">
                    Bank Account
                  </div>
                  <div class="s-text-neutral-400">
                    Details for deposits.
                  </div>
                </li>
              </ul>
            </div>
            <s-notification class="s-text-blue-900 s-text-sm s-w-full">
              Some fields are pre-filled from Chargebee Billing. Review and make
              corrections if necessary.
            </s-notification>

            <div class="s-flex s-flex-row s-justify-end s-w-full s-mt-4 s-gap-2" style="align-items: end;">
              <s-button
                :variant="secondary"
                @click="dismissCollectDetails"
              >
                Dismiss, I'll collect details
              </s-button>

              <s-button
                class="s-mt-1"
                variant="primary"
                @click="() => showTermsAndConditions()"
              >
                {{ getSubMerchantAgreementAcceptance ? 'Continue Onboarding': 'Start Onboarding' }}
              </s-button>
            </div>
          </section>

          <essentials-form
            v-if="currentStep === 0"
            @modified="completedUntilStep = -1"
            :should-resubmit="completedUntilStep < 0"
            @proceed="goNext"
          />
          <incremental-onboarding-business-address
            v-if="currentStep === 1"
            @modified="completedUntilStep = 0"
            :should-resubmit="completedUntilStep < 1"
            @proceed="goNext"
            @previous="goBack"
          />
          <ownership-overview
            v-if="currentStep === 2"
            @modified="completedUntilStep = 1"
            :should-resubmit="completedUntilStep < 2"
            @proceed="goNext"
            @previous="goBack"
          />
          <incremental-onboarding-business-profile
            v-if="currentStep === 3"
            @modified="completedUntilStep = 2"
            :should-resubmit="completedUntilStep < 3"
            @proceed="goNext"
            @previous="goBack"
          />
          <business-registration
            v-if="currentStep === 4"
            @modified="completedUntilStep = 3"
            :should-resubmit="completedUntilStep < 4"
            @proceed="goNext"
            @previous="goBack"
          />
          <incremental-onboarding-bank-account-for-payout
            v-if="currentStep === 5"
            @modified="completedUntilStep = 4"
            :should-resubmit="completedUntilStep < 5"
            @proceed="goNext"
            @previous="goBack"
          />
        </div>
      </div>

      <s-modal
        :control-variable="showDismissConfirmation"
        :dismissable="false"
        :primary-action="{
          label: $t('global.action.confirm'),
          action: dismiss
        }"
        :secondary-action="{
          label: $t('global.action.cancel'),
          action: () => showDismissConfirmation = false
        }"
        :heading="$t('dismissConfirm.title')"
        size="small"
        action-variant="danger"
        :description="$t('dismissConfirm.description')"
      >
        <template #body>
          <div class="s-text-left">
            {{ $t('dismissConfirm.description') }}
          </div>
        </template>
      </s-modal>


      <s-modal
        :dismissable="false"
        :control-variable="showSubmitPopup"
        :primary-action="{
          label: $t('global.action.iAccept'),
          action: acceptTnC,
          disabled: shouldDisableAcceptButton
        }"
        :secondary-action="{
          label: $t('termsAndCharges.exitSetup'),
          action: dismiss
        }"
        padding="xxlarge"
        :auto-close-on-action="false"
        :heading="$t('termsAndCharges.title')"
      >
        <template #body>
          <TermsAndConditionsV2 ref="termsComponent" @read-to-end="onReadToEnd" />
        </template>
      </s-modal>
      <IncrementalFooter />
    </div>
  </div>
</template>

<script>
import IncrementalOnboardingHeader from '@/components/onboarding/IncrementalOnboardingHeader';
import {mapActions, mapGetters, mapState} from 'vuex';
import TermsAndConditionsV2 from '@/components/onboarding/TermsAndConditionsV2';
import ProgressSidebar from '@/components/incremental-onboarding/ProgressSidebar';
import {
  PendoPrefixes,
  Tabs,
  OnboardingStatus, OnboardingStep
} from '@/app/utils/common/constants';
import {navigateTo} from '@/router';
import {RouteConstants} from '@/router/routeConstants';
import EssentialsForm from '@/components/incremental-onboarding/EssentialsForm.vue';
import IncrementalOnboardingBusinessAddress
  from "@/components/incremental-onboarding/IncrementalOnboardingBusinessAddress.vue";
import OwnershipOverview from "@/components/incremental-onboarding/OwnershipOverview.vue";
import IncrementalOnboardingBusinessProfile
  from "@/components/incremental-onboarding/IncrementalOnboardingBusinessProfile.vue";
import BusinessRegistration from "@/components/incremental-onboarding/BusinessRegistration.vue";
import IncrementalOnboardingBankAccountForPayout
  from "@/components/incremental-onboarding/IncrementalOnboardingBankAccountForPayout.vue";
import {openUrlInSameTab, getCbAppUrl} from "@/app/utils/common/functions/url-helper";
import "@/app/assets/style/incremental-onboarding-form.scss";
import {getAndStoreSession} from "@/service/sessionService";
import IncrementalFooter from "@/components/incremental-onboarding/IncrementalFooter.vue";

export default {
  name: 'IncrementalOnboardingView',
  components: {
    IncrementalFooter,
    IncrementalOnboardingBankAccountForPayout,
    BusinessRegistration,
    IncrementalOnboardingBusinessProfile,
    OwnershipOverview,
    IncrementalOnboardingBusinessAddress,
    ProgressSidebar,
    IncrementalOnboardingHeader,
    TermsAndConditionsV2,
    EssentialsForm,
  },
  data: function () {
    return {
      showSubmitPopup: false,
      showDismissConfirmation: false,
      sidebarNavCollapsed: false,
      steps: [
        {
          id: 'essentials',
          label: 'Essentials',
          description: 'Basic setup details.',
        },
        {
          id: 'address',
          label: 'Address',
          description: 'Enter your business address.',
        },
        {
          id: 'ownership',
          label: 'Ownership',
          description: 'Verify ownership details.',
        },
        {
          id: 'profile',
          label: 'Profile',
          description: 'Fill in your profile information.',
        },
        {
          id: 'business',
          label: 'Business Registration',
          description: 'Register your business.',
        },
        {
          id: 'bank',
          label: 'Business Bank Account',
          description: 'Optional: Add a bank account.',
        },
      ],
      currentStep: 0,
      completedUntilStep: -1,
      isAcceptDisabled: true
    };
  },
  computed: {
    ...mapState('onboarding', {
      currentTab: (state) => state.currentTab,
    }),
    ...mapGetters('agreementAcceptance', ['getSubMerchantAgreementAcceptance']),
    ...mapGetters('merchantPayfacDetails', ['isEligibleForMigration']),
    isAdmin() {
      return String(this.$router.currentRoute.query.adminMode) === 'true';
    },
    isMigrating() {
      return (
          this.$route.query?.migration === true && this.isEligibleForMigration
      );
    },
    ...mapGetters('app', ['isAdminMode', 'getMerchantId', 'getSessionPayfac',
      'getDraftMerchantId', 'getMerchantName', 'getCbSiteName']),
    ...mapGetters('merchantPayfacDetails', ['getOnboardingStatus', 'getOnboardingStep']),
    tabValidations() {
      return {
        [Tabs.businessProfile]: {
          valid: this.isBusinessProfileValid && this.isBusinessAddressValid,
          generateError: this.generateBusinessProfileTabErrors,
        },
        [Tabs.operations]: {
          valid:
              this.isBusinessStructureValid && this.isStakeholdersDetailsValid,
          generateError: this.generateOperationsTabErrors,
        },
        [Tabs.settlements]: {
          valid:
              this.isAdditionalUnderwritingDataValid &&
              this.isBankAccountForPayoutsValid,
          generateError: this.generateSettlementsTabErrors,
        },
      };
    },
    isConsentAccepted() {
      // Check if the consent checkbox in the child component is checked
      return this.$refs.termsComponent && 
             this.$refs.termsComponent.$refs.termsComponent && 
             this.$refs.termsComponent.$refs.termsComponent.recurringChargesConsentAccepted;
    },
    shouldDisableAcceptButton() {
      // Button should be disabled if user hasn't scrolled to end OR consent is not accepted
      return this.isAcceptDisabled || !this.isConsentAccepted;
    },
  },
  created() {
    this.tabs = Tabs;
  },
  mounted() {
    let onboardingStatus = this.getOnboardingStatus(this.getSessionPayfac);
    let onboardingStep = this.getOnboardingStep(this.getSessionPayfac);

    if ([OnboardingStep.SUBMITTED_SKIPPED_BANK_ACCOUNT,
          OnboardingStep.SUBMITTED_ADDED_BANK_ACCOUNT,
          OnboardingStep.ADDED_BANK_ACCOUNT].includes(onboardingStep)) {
      if (
          [
            OnboardingStatus.PROVISIONING,
            OnboardingStatus.REJECTED
          ].includes(onboardingStatus)) {
        navigateTo(RouteConstants.SETUP_PROGRESS)
        return;
      }

      if (onboardingStatus === OnboardingStatus.APPROVED) {
        navigateTo(RouteConstants.DASHBOARD)
        return;
      }
    }

    this.currentStep = this.getCurrentStep(onboardingStep);
    
    this.completedUntilStep = this.currentStep !== -1 ? this.currentStep - 1 : -1;
    this.validateAllFields();
  },
  methods: {
    async dismiss() {
      let onboardingStatus = this.getOnboardingStatus(this.getSessionPayfac);
      if (onboardingStatus === OnboardingStatus.NOT_INITIATED) {
        openUrlInSameTab(getCbAppUrl(this.getCbSiteName) + "/payment_gateways")
      } else {
        await getAndStoreSession();
        navigateTo(RouteConstants.SETUP_PROGRESS)
      }
    },
    dismissCollectDetails() {
      let onboardingStatus = this.getOnboardingStatus(this.getSessionPayfac);
      if (onboardingStatus === OnboardingStatus.NOT_INITIATED) {
        openUrlInSameTab(getCbAppUrl(this.getCbSiteName) + "/payment_gateways")
      } else {
        this.showDismissConfirmation = true;
      }
    },
    ...mapActions('onboarding', [
      'validateAllFields',
    ]),
    ...mapActions('app', ['updateAppState']),
    ...mapActions('agreementAcceptance', ['acceptAgreement']),
    async acceptTnC() {
      await this.acceptAgreement(true);
      this.showSubmitPopup = false
      this.currentStep = 0;
    },
    showTermsAndConditions() {
      if (!this.getSubMerchantAgreementAcceptance) {
        this.showSubmitPopup = true;
      } else {
        this.currentStep = 0;
      }
    },
    getCurrentStep(onboardingStep) {
      switch (onboardingStep) {
        case OnboardingStep.CREATED_BUSINESS_INFO:
          return 1;
        case OnboardingStep.UPDATED_BUSINESS_ADDRESS:
          return 2;
        case OnboardingStep.ADDED_CONTROL_PERSON:
        case OnboardingStep.ADDED_BENEFICIARY:
        case OnboardingStep.UPDATED_BENEFICIARY:
          return 3;
        case OnboardingStep.ADDED_BUSINESS_PROFILE:
          return 4;
        case OnboardingStep.ADDED_REGISTRATION_INFO:
          return 5;
        default:
          return -1;
      }
    },
    handleStepChange(index) {
      this.currentStep = index;
    },
    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    goNext() {
      if (this.currentStep < this.steps.length - 1) {
        this.completedUntilStep = Math.max(this.currentStep, this.completedUntilStep);
        this.currentStep++;
      }
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    getElementId(id) {
      return PendoPrefixes.CBpay + this.$route.name + '-' + id;
    },
    onReadToEnd() {
      // user has either seen the end or content wasn’t scrollable:
      this.isAcceptDisabled = false;
    },
  },
};
</script>
